import React, { useEffect, useState } from "react";
import { api, formatMXN } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Users, TrendingUp, Sparkles, Percent } from "lucide-react";

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

/**
 * Promoter "Mi equipo" report (used in PromoterDashboard).
 * Calls /promoter/my-team
 */
export default function PromoterTeam() {
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/promoter/my-team?month=${m}`);
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  return (
    <div className="space-y-5" data-testid="promoter-team-section">
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-200 via-emerald-300 to-emerald-500 grid place-items-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display text-2xl tracking-tight">Mi equipo de promotores</h3>
              <p className="text-xs text-muted-foreground">
                Promotores que invitaste · ganas un % de sus ventas mensuales.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Mes:</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="team-month-input" />
          </div>
        </div>

        {loading || !data ? (
          <div className="text-muted-foreground text-sm py-6">Cargando…</div>
        ) : (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              <div className="bg-white border border-border rounded-xl p-4">
                <div className="overline text-xs text-muted-foreground">Promotores referidos</div>
                <div className="font-display text-3xl mt-1">{data.team_size}</div>
                <div className="text-[10px] text-muted-foreground">{data.team_active_size} activos</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="overline text-xs text-muted-foreground">Producción del equipo</div>
                <div className="font-display text-3xl mt-1 text-emerald-700">{formatMXN(data.team_month_commissions_total)}</div>
                <div className="text-[10px] text-muted-foreground">{monthLabel(data.month)}</div>
              </div>
              <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
                <div className="overline text-xs text-muted-foreground">Tu % asignado</div>
                <div className="font-display text-3xl mt-1 text-[#5B21B6]">
                  {data.referral_bonus_pct}%
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {data.referral_bonus_mode === "monthly" ? "mes a mes" : "única vez"}
                  {data.referral_bonus_mode === "one_time" && data.referral_bonus_one_time_paid ? " · ya pagado" : ""}
                </div>
              </div>
              <div className="bg-gradient-to-br from-amber-200 to-amber-400 border border-amber-300 rounded-xl p-4 text-amber-950">
                <div className="overline text-xs">Tu bonus calculado</div>
                <div className="font-display text-3xl mt-1">{formatMXN(data.my_calculated_bonus)}</div>
                <div className="text-[10px] opacity-80">{monthLabel(data.month)}</div>
              </div>
            </div>

            {data.referral_bonus_pct === 0 && (
              <div className="mt-5 bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4 flex items-start gap-2 text-xs">
                <Sparkles className="w-4 h-4 mt-0.5 text-[#7C3AED] shrink-0" />
                <span className="text-zinc-700">
                  Aún no tienes un % asignado por el admin para tu equipo. Cuando lo configure, verás tus bonos calculados aquí automáticamente.
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Referrals table */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-display text-xl tracking-tight">Promotores que invitaste</h3>
        {loading || !data ? null : data.referrals.length === 0 ? (
          <div className="mt-4 text-center text-sm text-muted-foreground py-10 border-2 border-dashed border-border rounded-xl" data-testid="team-empty">
            Aún no has invitado a ningún promotor. Comparte tu nombre y pídeles que te pongan como invitador al registrarse.
          </div>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="team-table">
              <thead className="text-left text-muted-foreground border-b border-border">
                <tr><th className="py-2">Promotor</th><th>Teléfono</th><th>Comercios activos</th><th>Comisiones del mes</th><th>Tu bonus ({data.referral_bonus_pct}%)</th></tr>
              </thead>
              <tbody>
                {data.referrals.map((r) => {
                  const myCut = (r.month_commissions_amount * (data.referral_bonus_pct || 0)) / 100;
                  return (
                    <tr key={r.promoter_id} className="border-b border-border" data-testid={`team-row-${r.promoter_id}`}>
                      <td className="py-3">
                        <div className="font-medium">{r.name}</div>
                        {!r.is_active && <div className="text-[10px] text-rose-600">Inactivo</div>}
                      </td>
                      <td className="font-mono text-xs">{r.phone}</td>
                      <td>
                        <Badge variant="outline">{r.active_affiliates}</Badge>
                      </td>
                      <td className="font-medium">{formatMXN(r.month_commissions_amount)}<div className="text-[10px] text-muted-foreground">{r.month_commissions_count} pago(s)</div></td>
                      <td className="font-medium text-emerald-700 inline-flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        {formatMXN(myCut)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
